import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ErrorApi, api } from '../api/cliente';
import type { Rol, Sede, Usuario } from '../api/tipos';
import { useSesion } from '../auth/sesion';
import {
  Aviso,
  Campo,
  Cargando,
  Encabezado,
  Etiqueta,
  Seleccion,
  Tarjeta,
  Vacio,
  fechaHora,
} from '../ui/piezas';

const ALCANCES = [
  { valor: 'PROPIO', texto: 'Propio — solo lo suyo' },
  { valor: 'SEDE', texto: 'Sede — toda su oficina' },
  { valor: 'GLOBAL', texto: 'Global — toda la empresa' },
];

interface Borrador {
  email: string;
  nombre: string;
  apellido: string;
  cargo: string;
  telefono: string;
  sedeId: string;
}

const VACIO: Borrador = { email: '', nombre: '', apellido: '', cargo: '', telefono: '', sedeId: '' };

/**
 * La administración de raíz.
 *
 * Es la pantalla que permite dejar de operar con la cuenta sembrada al primer
 * arranque, que es lo que hoy hace todo el mundo y no debería.
 */
export default function Usuarios() {
  const cache = useQueryClient();
  const { puede, usuario: yo } = useSesion();
  const [forma, setForma] = useState<Borrador>(VACIO);
  const [abierto, setAbierto] = useState<string | null>(null);
  const [rol, setRol] = useState('');
  const [alcance, setAlcance] = useState('PROPIO');
  const [error, setError] = useState<unknown>(null);
  const [exito, setExito] = useState<string | null>(null);

  const usuarios = useQuery({
    queryKey: ['usuarios'],
    queryFn: () => api<Usuario[]>('/admin/usuarios'),
  });
  const roles = useQuery({
    queryKey: ['roles'],
    queryFn: () => api<Rol[]>('/admin/roles'),
    enabled: puede('rol:leer'),
  });

  /*
   * Hasta dónde llego yo.
   *
   * El servidor manda igual —comprueba lo mismo en cada petición— pero ofrecer
   * un botón que va a devolver 403 es hacerle perder el tiempo a alguien y
   * dejarle con la duda de si el sistema falla o él no puede.
   */
  const porCodigo = new Map((roles.data ?? []).map((r) => [r.codigo, r.nivel]));
  const miNivel = Math.max(0, ...(yo?.roles ?? []).map((r) => porCodigo.get(r.rol) ?? 0));
  const nivelDe = (u: Usuario) => Math.max(0, ...u.roles.map((r) => porCodigo.get(r.rol) ?? 0));

  const sedes = useQuery({
    queryKey: ['sedes'],
    queryFn: () => api<Sede[]>('/admin/sedes'),
  });

  const refrescar = () => void cache.invalidateQueries({ queryKey: ['usuarios'] });

  const crear = useMutation({
    mutationFn: () =>
      api<Usuario>('/admin/usuarios', {
        metodo: 'POST',
        cuerpo: {
          email: forma.email,
          nombre: forma.nombre,
          apellido: forma.apellido,
          cargo: forma.cargo || null,
          telefono: forma.telefono || null,
          sedeId: forma.sedeId,
        },
      }),
    onSuccess: () => {
      setError(null);
      setExito('Usuario creado. La contraseña se generó al azar: reiníciala para entregársela.');
      setForma(VACIO);
      refrescar();
    },
    onError: (e) => {
      setExito(null);
      setError(e);
    },
  });

  const asignar = useMutation({
    mutationFn: (usuarioId: string) =>
      api<Usuario>(`/admin/usuarios/${usuarioId}/roles`, {
        metodo: 'POST',
        // `rol`, no `rolId`, y el **codigo** del rol, no su identificador: la
        // API lo resuelve con `findByCodigo`. Se enviaba `{ rolId: <uuid> }`,
        // asi que `rol` llegaba nulo y su `@NotBlank` rechazaba la peticion.
        // Asignar un rol no habia funcionado nunca desde el panel.
        cuerpo: { rol, alcance, sedeId: null },
      }),
    onSuccess: () => {
      setError(null);
      setExito('Rol asignado.');
      refrescar();
    },
    onError: (e) => {
      setExito(null);
      setError(e);
    },
  });

  const errorApi = error instanceof ErrorApi ? error : null;

  return (
    <>
      <Encabezado titulo="Usuarios" nota="Quién entra al panel y con qué alcance." />

      <Aviso error={error} exito={exito} />

      {puede('usuario:crear') && (
        <Tarjeta titulo="Crear usuario">
          <div className="rejilla">
            <Campo
              etiqueta="Correo"
              tipo="email"
              requerido
              valor={forma.email}
              alCambiar={(v) => setForma({ ...forma, email: v })}
              error={errorApi?.mensajeDe('email')}
            />
            <Campo
              etiqueta="Nombre"
              requerido
              valor={forma.nombre}
              alCambiar={(v) => setForma({ ...forma, nombre: v })}
              error={errorApi?.mensajeDe('nombre')}
            />
            <Campo
              etiqueta="Apellido"
              requerido
              valor={forma.apellido}
              alCambiar={(v) => setForma({ ...forma, apellido: v })}
              error={errorApi?.mensajeDe('apellido')}
            />
            <Campo
              etiqueta="Cargo"
              valor={forma.cargo}
              alCambiar={(v) => setForma({ ...forma, cargo: v })}
            />
            <Campo
              etiqueta="Teléfono"
              valor={forma.telefono}
              alCambiar={(v) => setForma({ ...forma, telefono: v })}
            />
            <Seleccion
              etiqueta="Oficina"
              requerido
              valor={forma.sedeId}
              alCambiar={(v) => setForma({ ...forma, sedeId: v })}
              vacio="Elige una"
              opciones={(sedes.data ?? []).map((s) => ({ valor: s.id, texto: s.nombre }))}
            />
          </div>
          <div className="fila fila--fin" style={{ marginTop: 14 }}>
            <button
              disabled={crear.isPending || !forma.email || !forma.nombre || !forma.sedeId}
              onClick={() => crear.mutate()}
            >
              Crear
            </button>
          </div>
          <p className="campo__ayuda">
            Nace sin roles: sin uno, puede entrar pero no ve nada. Asígnaselos abajo.
          </p>
        </Tarjeta>
      )}

      <Tarjeta titulo="Equipo">
        {usuarios.isPending ? (
          <Cargando />
        ) : (usuarios.data ?? []).length === 0 ? (
          <Vacio>No hay usuarios visibles con tu alcance.</Vacio>
        ) : (
          <div className="tabla-marco">
            <table>
              <thead>
                <tr>
                  <th>Persona</th>
                  <th>Oficina</th>
                  <th>Roles</th>
                  <th>Estado</th>
                  <th>Último acceso</th>
                  {puede('rol:editar') && <th />}
                </tr>
              </thead>
              <tbody>
                {(usuarios.data ?? []).map((u) => (
                  <tr key={u.id}>
                    <td>
                      <div>
                        {u.nombre} {u.apellido}
                        {u.id === yo?.id && <small style={{ opacity: 0.6 }}> · tú</small>}
                      </div>
                      <small style={{ opacity: 0.7 }}>{u.email}</small>
                    </td>
                    <td>{u.sedeCodigo}</td>
                    <td>
                      {u.roles.length === 0 ? (
                        <span style={{ color: '#B77714' }}>sin roles</span>
                      ) : (
                        u.roles.map((r) => (
                          <div key={`${r.rol}-${r.sedeId ?? ''}`}>
                            {r.nombre} <small style={{ opacity: 0.7 }}>({r.alcance.toLowerCase()})</small>
                          </div>
                        ))
                      )}
                    </td>
                    <td>
                      <Etiqueta>{u.bloqueado ? 'SUSPENDIDO' : u.estado}</Etiqueta>
                    </td>
                    <td>{fechaHora(u.ultimoAccesoEn)}</td>
                    {puede('rol:editar') && (
                      <td>
                        {nivelDe(u) > miNivel ? (
                          <small style={{ opacity: 0.6 }}>Por encima de tu nivel</small>
                        ) : (
                        <button
                          className="plano"
                          onClick={() => setAbierto(abierto === u.id ? null : u.id)}
                        >
                          {abierto === u.id ? 'Cerrar' : 'Dar rol'}
                        </button>
                        )}
                        {abierto === u.id && (
                          <div style={{ marginTop: 8, minWidth: 240 }}>
                            <Seleccion
                              etiqueta="Rol"
                              valor={rol}
                              alCambiar={setRol}
                              vacio="Elige uno"
                              opciones={(roles.data ?? []).filter((r) => r.nivel <= miNivel).map((r) => ({
                                valor: r.codigo,
                                texto: r.nombre,
                              }))}
                            />
                            <div style={{ height: 8 }} />
                            <Seleccion
                              etiqueta="Alcance"
                              ayuda="No puedes conceder más del que tienes"
                              valor={alcance}
                              alCambiar={setAlcance}
                              opciones={ALCANCES}
                            />
                            <div style={{ height: 8 }} />
                            <button disabled={!rol} onClick={() => asignar.mutate(u.id)}>
                              Asignar
                            </button>
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Tarjeta>
    </>
  );
}
